import { availableAuditEntriesAtom } from '@/global-store';
import {
  Combobox,
  type FloatingLabelProps,
  type ComboboxSingleProps,
} from '@eye-able/ui';
import { useAtom } from 'jotai';
import { isNil } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@helpers/cn';
import { type AuditData } from '@eye-able/types';
import { useLimitedComboboxItems } from '../hooks/useLimitedComboboxItems';

interface DomainSelectProps extends Partial<ComboboxSingleProps> {
  className?: string;
  excludeEntries?: number[];
  fullWidth?: boolean;
  initialEntryId?: string | number | null;
  labelProps?: FloatingLabelProps;
  onChange?: (entry?: AuditData) => void;
}

interface DomainOption {
  value: string;
  label: string;
}

const DomainSelect = ({
  className,
  excludeEntries = [],
  fullWidth,
  initialEntryId,
  labelProps,
  onChange,
  ...rest
}: DomainSelectProps) => {
  const { t } = useTranslation();
  const [availableAuditEntries] = useAtom(availableAuditEntriesAtom);
  const [searchValue, setSearchValue] = useState('');
  const [entryId, setEntryId] = useState(() =>
    normalizeEntryId(initialEntryId),
  );

  const domains = useMemo(() => {
    return prepareOptions(availableAuditEntries, excludeEntries);
  }, [availableAuditEntries, excludeEntries]);

  const entryMap = useMemo(() => {
    const map = new Map<string, AuditData>();

    (availableAuditEntries ?? []).forEach((entry) => {
      map.set(String(entry.id), entry);
    });

    return map;
  }, [availableAuditEntries]);

  const { comboboxItems } = useLimitedComboboxItems({
    items: domains,
    searchValue,
    selectedValue: entryId,
  });

  const handleSelect = (nextValue: string) => {
    if (!nextValue) return;

    setEntryId(nextValue);

    const selectedEntry = entryMap.get(nextValue);
    onChange?.(selectedEntry);
  };

  if (!domains?.length) return null;

  return (
    <div className={cn(className)}>
      <Combobox
        disabled={domains.length === 1}
        fullWidth={fullWidth}
        items={comboboxItems}
        labelProps={{ float: 'top', label: t('domain'), ...labelProps }}
        onSearchChange={setSearchValue}
        onSelect={handleSelect}
        placeholder={t('selectDomain')}
        search={searchValue}
        filter={filterFn}
        value={entryId ?? ''}
        {...rest}
      />
    </div>
  );
};

function filterFn(value: string, query: string) {
  if (value.includes(query)) return 1;
  return 0;
}

function prepareOptions(
  availableAuditEntries: AuditData[] | undefined,
  excludeEntries: number[],
) {
  if (!availableAuditEntries) return [];

  const result: Array<DomainOption> = [];
  const countMap: Record<string, number> = {};
  const excludeSet = new Set((excludeEntries ?? []).map(String));

  availableAuditEntries.forEach((entry) => {
    const normalizedId = String(entry.id);

    if (excludeSet.has(normalizedId)) return;

    let label = entry.domain;

    // show the full domain only for admins
    if (availableAuditEntries.length < 99) {
      label = shortenDomain(label);
    }

    if (countMap[label]) {
      countMap[label]++;
      label = `${label} (${countMap[label]})`;
    } else {
      countMap[label] = 1;
    }

    result.push({ value: normalizedId, label });
  });

  return result;
}

function shortenDomain(domain: string): string {
  let label = domain;

  if (label.startsWith('http://')) {
    label = label.slice(7);
  } else if (label.startsWith('https://')) {
    label = label.slice(8);
  }

  if (label.endsWith('/')) {
    label = label.slice(0, -1);
  }

  return label;
}

function normalizeEntryId(
  value: DomainSelectProps['initialEntryId'],
): string | undefined {
  return isNil(value) ? undefined : String(value);
}

export default DomainSelect;
